import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { alertService } from '@/services/alert.service'
import { deviceService } from '@/services/device.service'
import { userService } from '@/services/user.service'
import { ROUTES } from '@/config/routes'
import type { Device } from '@/types/device.types'
import type { User } from '@/types/user.types'
import type { ComparisonOperator, SeverityLevel } from '@/types/alert.types'

const alertCreateSchema = z.object({
  alertName: z.string().min(1, 'Alert name is required'),
  deviceType: z.enum(['watersense', 'rk900', 'dht11soil'] as const).optional(),
  measurementColumn: z.string().min(1, 'Measurement column is required'),
  comparisonOperator: z.enum(['>', '<', '>=', '<=', '==', '!='] as const),
  thresholdValue: z.coerce.number({ invalid_type_error: 'Threshold must be a number' }),
  severityLevel: z.enum(['info', 'warning', 'critical'] as const),
  alertMessage: z.string().min(1, 'Alert message is required'),
  alertLinkedUsers: z.boolean().default(false),
  isActive: z.boolean().default(true),
  deviceIds: z.array(z.string()).min(1, 'Select at least one device'),
  recipientUserIds: z.array(z.string())
})

type AlertCreateFormData = z.infer<typeof alertCreateSchema>

const COMPARISON_OPERATORS: { value: ComparisonOperator; label: string }[] = [
  { value: '>', label: 'Greater than' },
  { value: '<', label: 'Less than' },
  { value: '>=', label: 'Greater than or equal' },
  { value: '<=', label: 'Less than or equal' },
  { value: '==', label: 'Equal to' },
  { value: '!=', label: 'Not equal to' }
]

const SEVERITY_LEVELS: { value: SeverityLevel; label: string }[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' }
]

// Common measurement columns per device type
const MEASUREMENT_COLUMNS: Record<string, { value: string; label: string }[]> = {
  watersense: [
    { value: 'temperature', label: 'Temperature' },
    { value: 'humidity', label: 'Humidity' },
    { value: 'moisture', label: 'Moisture' },
    { value: 'battery', label: 'Battery Level' }
  ],
  rk900: [
    { value: 'temperature', label: 'Temperature' },
    { value: 'humidity', label: 'Humidity' },
    { value: 'pressure', label: 'Pressure' },
    { value: 'battery', label: 'Battery Level' }
  ],
  dht11soil: [
    { value: 'temperature', label: 'Temperature' },
    { value: 'humidity', label: 'Humidity' },
    { value: 'soil_moisture', label: 'Soil Moisture' },
    { value: 'battery', label: 'Battery Level' }
  ]
}

export const AlertNewPage = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<Device[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [isLoadingDevices, setIsLoadingDevices] = useState(true)
  const [isLoadingUsers, setIsLoadingUsers] = useState(true)
  const [selectedDeviceType, setSelectedDeviceType] = useState<string | undefined>()

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<AlertCreateFormData>({
    resolver: zodResolver(alertCreateSchema),
    defaultValues: {
      severityLevel: 'warning',
      isActive: true,
      alertLinkedUsers: false,
      deviceIds: [],
      recipientUserIds: []
    }
  })

  const watchedDeviceType = watch('deviceType')

  // Fetch devices and users on mount
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await deviceService.getDevices()
        setDevices(response.devices || [])
      } catch (err: any) {
        setError('Failed to load devices')
        console.error('Failed to fetch devices:', err)
      } finally {
        setIsLoadingDevices(false)
      }
    }

    const fetchUsers = async () => {
      try {
        const response = await userService.getUsers()
        setUsers(response.users || [])
      } catch (err: any) {
        console.error('Failed to fetch users:', err)
      } finally {
        setIsLoadingUsers(false)
      }
    }

    fetchDevices()
    fetchUsers()
  }, [])

  // Update measurement columns when device type changes
  useEffect(() => {
    setSelectedDeviceType(watchedDeviceType)
    if (watchedDeviceType) {
      // Clear measurement column when device type changes
      setValue('measurementColumn', '')
    }
  }, [watchedDeviceType, setValue])

  const onSubmit = async (data: AlertCreateFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      // Convert camelCase to snake_case for API
      const apiData = {
        alert_name: data.alertName,
        device_type: data.deviceType,
        measurement_column: data.measurementColumn,
        comparison_operator: data.comparisonOperator,
        threshold_value: data.thresholdValue,
        severity_level: data.severityLevel,
        alert_message: data.alertMessage,
        alert_linked_users: data.alertLinkedUsers,
        is_active: data.isActive,
        device_ids: data.deviceIds,
        recipient_user_ids: data.recipientUserIds
      }

      await alertService.createAlert(apiData as any)
      navigate(ROUTES.ALERTS)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create alert. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Filter devices by type if selected
  const filteredDevices = selectedDeviceType
    ? devices.filter(d => d.deviceType === selectedDeviceType)
    : devices

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <CardTitle>Create New Alert</CardTitle>
          <CardDescription>Configure a new alert for monitoring devices</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="alertName">Alert Name *</Label>
                <Input
                  id="alertName"
                  type="text"
                  placeholder="High Temperature Alert"
                  {...register('alertName')}
                />
                {errors.alertName && (
                  <p className="text-sm text-destructive">{errors.alertName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="deviceType">Device Type (Optional)</Label>
                <Controller
                  name="deviceType"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="deviceType">
                        <SelectValue placeholder="All device types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">All device types</SelectItem>
                        <SelectItem value="watersense">WaterSense</SelectItem>
                        <SelectItem value="rk900">RK900</SelectItem>
                        <SelectItem value="dht11soil">DHT11 Soil</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="measurementColumn">Measurement Column *</Label>
                {selectedDeviceType && MEASUREMENT_COLUMNS[selectedDeviceType] ? (
                  <Controller
                    name="measurementColumn"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger id="measurementColumn">
                          <SelectValue placeholder="Select measurement" />
                        </SelectTrigger>
                        <SelectContent>
                          {MEASUREMENT_COLUMNS[selectedDeviceType].map(col => (
                            <SelectItem key={col.value} value={col.value}>
                              {col.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                ) : (
                  <Input
                    id="measurementColumn"
                    type="text"
                    placeholder="e.g., temperature, humidity"
                    {...register('measurementColumn')}
                  />
                )}
                {errors.measurementColumn && (
                  <p className="text-sm text-destructive">{errors.measurementColumn.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="comparisonOperator">Comparison *</Label>
                <Controller
                  name="comparisonOperator"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="comparisonOperator">
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPARISON_OPERATORS.map(op => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.comparisonOperator && (
                  <p className="text-sm text-destructive">{errors.comparisonOperator.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="thresholdValue">Threshold Value *</Label>
                <Input
                  id="thresholdValue"
                  type="number"
                  step="0.01"
                  placeholder="e.g., 25.5"
                  {...register('thresholdValue')}
                />
                {errors.thresholdValue && (
                  <p className="text-sm text-destructive">{errors.thresholdValue.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="severityLevel">Severity Level *</Label>
                <Controller
                  name="severityLevel"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="severityLevel">
                        <SelectValue placeholder="Select severity" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITY_LEVELS.map(level => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alertMessage">Alert Message *</Label>
              <Textarea
                id="alertMessage"
                placeholder="Enter the message to be sent when this alert is triggered"
                {...register('alertMessage')}
              />
              {errors.alertMessage && (
                <p className="text-sm text-destructive">{errors.alertMessage.message}</p>
              )}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Devices *</Label>
                <div className="border rounded-md p-4 max-h-48 overflow-y-auto space-y-2">
                  {isLoadingDevices ? (
                    <p className="text-sm text-muted-foreground">Loading devices...</p>
                  ) : filteredDevices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No devices available</p>
                  ) : (
                    <Controller
                      name="deviceIds"
                      control={control}
                      render={({ field }) => (
                        <>
                          {filteredDevices.map(device => (
                            <div key={device.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`device-${device.id}`}
                                checked={field.value?.includes(device.id)}
                                onCheckedChange={(checked) => {
                                  const newValue = checked
                                    ? [...(field.value || []), device.id]
                                    : field.value?.filter(id => id !== device.id) || []
                                  field.onChange(newValue)
                                }}
                              />
                              <Label
                                htmlFor={`device-${device.id}`}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {device.name} ({device.serialNumber})
                              </Label>
                            </div>
                          ))}
                        </>
                      )}
                    />
                  )}
                </div>
                {errors.deviceIds && (
                  <p className="text-sm text-destructive">{errors.deviceIds.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Select Recipients (Optional)</Label>
                <div className="border rounded-md p-4 max-h-48 overflow-y-auto space-y-2">
                  {isLoadingUsers ? (
                    <p className="text-sm text-muted-foreground">Loading users...</p>
                  ) : users.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No users available</p>
                  ) : (
                    <Controller
                      name="recipientUserIds"
                      control={control}
                      render={({ field }) => (
                        <>
                          {users.map(user => (
                            <div key={user.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`user-${user.id}`}
                                checked={field.value?.includes(user.id)}
                                onCheckedChange={(checked) => {
                                  const newValue = checked
                                    ? [...(field.value || []), user.id]
                                    : field.value?.filter(id => id !== user.id) || []
                                  field.onChange(newValue)
                                }}
                              />
                              <Label
                                htmlFor={`user-${user.id}`}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {user.firstName} {user.lastName} ({user.email})
                              </Label>
                            </div>
                          ))}
                        </>
                      )}
                    />
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Controller
                  name="alertLinkedUsers"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="alertLinkedUsers"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label htmlFor="alertLinkedUsers">
                  Alert linked users (notify users linked to the device's account)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="isActive"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label htmlFor="isActive">
                  Activate alert immediately
                </Label>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(ROUTES.ALERTS)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating Alert...' : 'Create Alert'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}